<template>
  <div class="message-bell-wrapper">
    <el-badge :value="unreadCount" :max="99" :hidden="unreadCount === 0" class="bell-badge">
      <i class="el-icon-bell bell-icon" @click.stop="togglePopover"></i>
    </el-badge>
    <transition name="el-zoom-in-top">
      <div v-show="popoverVisible" class="message-popover-box">
        <div class="message-popover-content">
          <div class="popover-header">
            <span>消息通知</span>
            <el-button type="text" size="mini" @click="markAllRead" v-if="unreadCount > 0">
              全部已读
            </el-button>
          </div>
          <div class="message-list" v-if="messageList.length > 0">
            <div
              v-for="msg in messageList"
              :key="msg.id"
              class="message-item"
              :class="{ 'unread': msg.isRead === 0 }"
              @click="handleMessageClick(msg)"
            >
              <div class="message-content">
                <span v-if="msg.isRead === 0" class="unread-dot"></span>
                {{ msg.content }}
              </div>
              <div class="message-time">{{ formatTime(msg.createTime) }}</div>
            </div>
          </div>
          <div class="no-message" v-else>
            暂无消息
          </div>
          <div class="popover-footer" v-if="messageList.length > 0">
            <el-button type="text" size="mini" @click="goToMessageList">
              查看全部消息
            </el-button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getUnreadCount, getMessageList, markAsRead, markAllAsRead } from "@/api/message";

export default {
  name: "MessageBell",
  data() {
    return {
      unreadCount: 0,
      messageList: [],
      popoverVisible: false,
      timer: null,
    };
  },
  mounted() {
    this.loadUnreadCount();
    // 每30秒刷新一次
    this.timer = setInterval(() => {
      this.loadUnreadCount();
      if (this.popoverVisible) {
        this.loadMessageList();
      }
    }, 30000);
    // 点击外部关闭弹窗
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
    }
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    togglePopover() {
      this.popoverVisible = !this.popoverVisible;
      if (this.popoverVisible) {
        this.loadMessageList();
      }
    },
    handleClickOutside(e) {
      const wrapper = this.$el;
      if (wrapper && !wrapper.contains(e.target)) {
        this.popoverVisible = false;
      }
    },
    async loadUnreadCount() {
      try {
        const res = await getUnreadCount();
        if (res.code === 1000) {
          this.unreadCount = res.data.count || 0;
        }
      } catch (error) {
        console.error('获取未读消息数量失败:', error);
      }
    },
    async loadMessageList() {
      try {
        const res = await getMessageList({
          current: 1,
          size: 10,
        });
        if (res.code === 1000) {
          this.messageList = res.data.records || [];
        }
      } catch (error) {
        console.error('获取消息列表失败:', error);
      }
    },
    async handleMessageClick(msg) {
      if (msg.isRead === 0) {
        try {
          await markAsRead({ id: msg.id });
          msg.isRead = 1;
          this.unreadCount = Math.max(0, this.unreadCount - 1);
        } catch (error) {
          console.error('标记已读失败:', error);
        }
      }
      this.jumpToBizPage(msg);
    },
    jumpToBizPage(msg) {
      const userInfo = this.$store.state.system.userInfoweb;
      const userType = userInfo ? userInfo.type : null;
      let targetPath = null;
      // 企业端：产品申请、审核结果、授信结果 → 融资交易
      if ((msg.bizType === 1 || msg.bizType === 2 || msg.bizType === 3) && userType === 4) {
        targetPath = '/workbench/trading';
      }
      // 金融机构端：所有消息类型 → 放贷交易
      if (userType === 5) {
        targetPath = '/financInstitutionPC/myProduct/lendingTransactions';
      }
      if (targetPath) {
        if (this.$route.path === targetPath) {
          // 已在目标页面，直接打开详情
          this.openDetail(targetPath, msg.bizId);
        } else {
          this.$router.push({ path: targetPath, query: { highlightId: msg.bizId } }).catch(() => {});
        }
      }
      this.popoverVisible = false;
    },
    openDetail(path, bizId) {
      // 通知当前页面打开对应订单详情
      window.dispatchEvent(new CustomEvent('openOrderDetail', { detail: { id: bizId } }));
    },
    async markAllRead() {
      try {
        await markAllAsRead();
        this.unreadCount = 0;
        this.messageList.forEach(msg => { msg.isRead = 1; });
        this.$message.success('已全部标记为已读');
      } catch (error) {
        console.error('全部标记已读失败:', error);
      }
    },
    goToMessageList() {
      const userInfo = this.$store.state.system.userInfoweb;
      const userType = userInfo ? userInfo.type : null;
      if (userType === 5) {
        this.$router.push('/financInstitutionPC/message');
      } else {
        this.$router.push('/workbench/message');
      }
      this.popoverVisible = false;
    },
    formatTime(timestamp) {
      if (!timestamp) return '';
      const date = new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      if (diff < 60000) return '刚刚';
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前';
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前';
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}`;
    },
  },
};
</script>

<style scoped>
.message-bell-wrapper {
  display: inline-block;
  margin-right: 15px;
  position: relative;
}
.bell-badge {
  cursor: pointer;
}
.bell-icon {
  font-size: 20px;
  color: #606266;
  vertical-align: middle;
}
.bell-icon:hover {
  color: #409EFF;
}
.message-popover-box {
  position: absolute;
  top: 40px;
  right: 0;
  z-index: 2000;
  width: 340px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
}
.message-popover-content {
  min-width: 300px;
}
.popover-header {
  padding: 12px 15px;
  border-bottom: 1px solid #ebeef5;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 14px;
}
.message-list {
  max-height: 360px;
  overflow-y: auto;
}
.message-item {
  padding: 12px 15px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: background 0.3s;
}
.message-item:hover {
  background: #f5f7fa;
}
.message-item.unread {
  background: #ecf5ff;
  border-left: 3px solid #409EFF;
}
.message-item.unread:hover {
  background: #d9ecff;
}
.message-content {
  font-size: 14px;
  color: #303133;
  margin-bottom: 5px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
}
.unread-dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  background: #409EFF;
  border-radius: 50%;
  margin-right: 6px;
  margin-top: 6px;
  flex-shrink: 0;
}
.message-time {
  font-size: 12px;
  color: #909399;
}
.no-message {
  padding: 40px 15px;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
.popover-footer {
  padding: 10px 15px;
  border-top: 1px solid #ebeef5;
  text-align: center;
}
</style>
